import React, { useEffect, useState } from "react";
import { workflowService } from "../../services/api/workflow.service";
import type { RequeryRemark } from "../../types/workflow.types";
import "../../styles/pages/History.css";

interface QueryRemarksProps {
  entityName: string;
  recordId?: number;
  isOpen: boolean;
  onClose: () => void;
}

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const QueryRemarks: React.FC<QueryRemarksProps> = ({
  entityName,
  recordId,
  isOpen,
  onClose,
}) => {
  const [remarks, setRemarks] = useState<RequeryRemark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !recordId) return;

    const fetchRemarks = async () => {
      try {
        setLoading(true);
        setError("");
        const result = await workflowService.getRequeryRemarks(
          entityName,
          recordId,
        );
        setRemarks(Array.isArray(result) ? result : []);
      } catch (err: any) {
        setError(err?.message || "Failed to load query remarks");
      } finally {
        setLoading(false);
      }
    };

    fetchRemarks();
  }, [entityName, recordId, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="history-drawer-overlay" onClick={onClose}>
      <div
        className="history-drawer-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="history-drawer-header">
          <h3 className="history-drawer-title">Query Remarks</h3>
          <button
            type="button"
            className="history-drawer-close"
            onClick={onClose}
            aria-label="Close query remarks"
          >
            ✕
          </button>
        </div>

        <div className="history-drawer-body">
          {loading && (
            <div className="history-drawer-state">Loading query remarks...</div>
          )}
          {!loading && error && (
            <div className="history-drawer-state history-drawer-state--error">
              {error}
            </div>
          )}
          {!loading && !error && remarks.length === 0 && (
            <div className="history-drawer-state">No query remarks found.</div>
          )}

          {!loading && !error && remarks.length > 0 && (
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Remark</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {remarks.map((item) => (
                    <tr key={item.id}>
                      <td>{item.remark || "—"}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryRemarks;
